'use client';

/*
Bloc "Devenez hôte, estimez vos gains" de la homepage v5.
Barème en dur, calibré sur les prix production HT et la fréquence
de réservation observés (Attio, juillet 2026) — repris du design export.
*/
import { useState } from 'react';
import Image from 'next/image';
import YouTubeModal from '@/core/videos';

const BAREME = {
  apartment: { base: 350, days: 3 },
  house: { base: 600, days: 3 },
  loft: { base: 450, days: 5 },
  studio: { base: 700, days: 8 },
  warehouse: { base: 800, days: 4 },
  castle: { base: 1800, days: 2 },
  mansion: { base: 1500, days: 3 },
  boat: { base: 800, days: 2 },
  hotel: { base: 1000, days: 3 },
  gallery: { base: 700, days: 3 },
  offices: { base: 550, days: 3 },
  restaurant: { base: 650, days: 3 },
  outdoor: { base: 400, days: 2 },
  monument: { base: 1300, days: 2 },
  kitchen: { base: 600, days: 3 },
  gym: { base: 550, days: 3 },
  workshop: { base: 450, days: 3 },
};

const TYPE_ORDER = ['apartment', 'house', 'loft', 'castle', 'mansion', 'boat', 'hotel', 'gallery', 'restaurant', 'studio', 'warehouse', 'outdoor', 'monument', 'kitchen', 'gym', 'offices', 'workshop'];

const CITY_COEF = {
  paris: 1.4, idf: 1.1, lyon: 1, marseille: 1, bordeaux: 1, other: 0.8,
};
const CITY_ORDER = ['paris', 'idf', 'lyon', 'marseille', 'bordeaux', 'other'];

const LOCALE_BY_LANG = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES' };

const SURFACE_MIN = 50;
const SURFACE_MAX = 1000;

export function estimateHostGains({ type, city, surface }) {
  const b = BAREME[type] || BAREME.apartment;
  let surfCoef = 2.5;
  if (surface <= 60) surfCoef = 0.7;
  else if (surface <= 120) surfCoef = 1;
  else if (surface <= 250) surfCoef = 1.4;
  else if (surface <= 500) surfCoef = 1.9;
  const price = Math.max(200, Math.round((b.base * (CITY_COEF[city] || 1) * surfCoef) / 50) * 50);
  let dayCoef = 1;
  if (city === 'paris') dayCoef = 1.5;
  else if (city === 'idf') dayCoef = 1.2;
  const days = Math.max(1, Math.round(b.days * dayCoef));
  return { price, days, monthly: price * days };
}

// Bouton play : ouvre la vidéo dans la modale du site, sans quitter la page.
const PlayButton = YouTubeModal(({ openModalYoutube, label }) => (
  <button
    type="button"
    onClick={openModalYoutube}
    aria-label={label}
    className="flex size-14 shrink-0 items-center justify-center rounded-full bg-white shadow-[0_12px_28px_rgba(19,14,27,0.3)] transition duration-200 hover:scale-105"
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#130e1b" aria-hidden="true"><polygon points="8 5 20 12 8 19 8 5" /></svg>
  </button>
));

const selectClass = 'w-full cursor-pointer appearance-none rounded-lg border border-was-ink/12 bg-white py-3 pl-3.5 pr-10 text-sm font-medium text-was-ink outline-0 focus:border-was-violet focus:ring-0';

function Chevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-was-ink" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export default function HostSimulator({
  tx, lang, onBecomeHost, videoId,
}) {
  const [type, setType] = useState('apartment');
  const [city, setCity] = useState('paris');
  const [surface, setSurface] = useState(100);
  const { price, days, monthly } = estimateHostGains({ type, city, surface });
  const locale = LOCALE_BY_LANG[lang] || LOCALE_BY_LANG.fr;
  const fmt = (n) => n.toLocaleString(locale);
  const pct = ((surface - SURFACE_MIN) / (SURFACE_MAX - SURFACE_MIN)) * 100;
  const types = tx('host.types') || {};
  const cities = tx('host.cities') || {};

  return (
    <div className="grid overflow-hidden rounded-xl border border-was-ink/8 bg-white lg:grid-cols-2">
      <div className="relative min-h-[280px] lg:min-h-[340px]">
        <Image src="/img/home-v5/temoignage-hote.jpg" alt={tx('host.posterAlt')} fill sizes="(max-width: 1024px) 100vw, 600px" className="object-cover object-[50%_50%]" />
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-b from-transparent to-was-ink/60 px-5 py-4">
          <PlayButton videoId={videoId} label={tx('host.videoAria')} />
          <span className="text-sm font-normal text-white [text-shadow:0_1px_8px_rgba(19,14,27,0.5)]">{tx('host.videoLabel')}</span>
        </div>
      </div>
      <div className="flex min-w-0 flex-col justify-center gap-3 p-7 lg:p-12">
        <div className="text-sm font-normal text-was-muted first-letter:uppercase">{tx('host.kicker')}</div>
        <div className="font-heading text-[30px] font-extrabold uppercase leading-none tracking-[-0.01em] text-was-ink tabular-nums md:text-[38px] lg:text-[44px]">
          {fmt(monthly)}
          {' '}
          {tx('host.perMonth')}
        </div>
        <div className="mt-2 flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-2">
              <span className="text-sm font-normal text-was-ink">{tx('host.typeLabel')}</span>
              <span className="relative block">
                <select value={type} onChange={(e) => setType(e.target.value)} className={selectClass}>
                  {TYPE_ORDER.map((k) => <option key={k} value={k}>{types[k] || k}</option>)}
                </select>
                <Chevron />
              </span>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-sm font-normal text-was-ink">{tx('host.cityLabel')}</span>
              <span className="relative block">
                <select value={city} onChange={(e) => setCity(e.target.value)} className={selectClass}>
                  {CITY_ORDER.map((k) => <option key={k} value={k}>{cities[k] || k}</option>)}
                </select>
                <Chevron />
              </span>
            </label>
          </div>
          <label className="flex flex-col gap-2.5">
            <span className="flex items-center justify-between text-sm font-normal text-was-ink">
              {tx('host.surfaceLabel')}
              <span className="text-was-muted tabular-nums">{`${surface} m²`}</span>
            </span>
            <input
              type="range"
              className="was-slider w-full"
              min={SURFACE_MIN}
              max={SURFACE_MAX}
              step={10}
              value={surface}
              onChange={(e) => setSurface(Number(e.target.value))}
              style={{ background: `linear-gradient(90deg, #8760ff ${pct}%, rgba(19,14,27,0.1) ${pct}%)` }}
            />
          </label>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg bg-was-cream px-4 py-3.5 text-sm text-was-slate">
            <span>
              {tx('host.estimated')}
              {' '}
              <strong className="font-bold text-was-ink">{`${fmt(price)} ${tx('host.perDay')}`}</strong>
            </span>
            <span>
              {'≈ '}
              <strong className="font-bold text-was-ink">{`${days} ${tx('host.daysPerMonth')}`}</strong>
            </span>
          </div>
        </div>
        <div className="mt-3">
          <button
            type="button"
            onClick={onBecomeHost}
            className="inline-flex items-center gap-2 rounded-lg bg-was-ink px-6 py-3 text-[15px] font-normal text-white transition-colors duration-200 hover:bg-was-violet"
          >
            {tx('host.cta')}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}
